/**
 * Typed v1 event catalog + public tracking API (RFC-0974 §7).
 *
 * Instrumentation sites import ONLY the typed `track*` helpers below, so a call
 * site can never emit an undeclared event name or property shape. Each helper
 * encodes its trust tier (authenticated ⇒ the emitter attaches the GoTrue JWT and
 * the collector derives `user_id`; pre-auth ⇒ anonymous / session-scoped).
 *
 * Naming: every event is prefixed with its UI view (`landing_page_` / `auth_` /
 * `canvas_` / `dashboard_`) — replaces a separate `surface` field (Nan's feedback).
 * Properties are minimized — ids / types / counts only; NEVER prompts, message
 * text, filenames, emails, or signed URLs.
 */

import { getAppVersion } from './appVersion'
import type { ErrorName } from './errorClass'
import { emitBeacon, emitEvent, severSession } from './eventsClient'
import {
  CLIENT_ANALYTICS_EVENT_AUTHENTICATED,
  type AppErrorUncaughtProps,
  type AppInpReportedProps,
  type AuthSignedInProps,
  type AuthSigninFailedProps,
  type AuthSignupClickedProps,
  type AuthSignupSubmittedProps,
  type CanvasAssetLoadFinishedProps,
  type CanvasClipAddedToTimelineProps,
  type CanvasInsufficientTokensBlockProps,
  type CanvasLoadFinishedProps,
  type CanvasNodeRenderFailedProps,
  type CanvasVariantKeptProps,
  type CheckoutInitiateClickProps,
  type CheckoutRedirectBlockProps,
  type CheckoutRedirectSubmitProps,
  type CheckoutReturnFailProps,
  type CheckoutReturnSuccessProps,
  type CheckoutReturnUnmatchedProps,
  type ClientAnalyticsEventName,
  type ClientAnalyticsEventProps,
  type LandingPagePromptConsoleStartedProps,
  type LandingPagePromptSubmittedProps,
  type LandingPageViewProps,
  type NuxTooltipDismissedProps,
  type NuxTooltipShownProps,
  type SettingsTokenCatalogViewProps,
  type SettingsUsageAndTokensViewProps,
  type TokenCatalogOptionClickProps,
} from './generated/analyticsEvents'

// Enum unions are generated from schemas/analytics/ (RFC-1577 §7) — re-exported
// here so call sites keep importing them from one place.
export type {
  AppArea,
  AssetKind,
  AssetTrigger,
  AuthMethod,
  BlockedAction,
  CatalogSource,
  CreditsEntryPoint,
  DownloadKind,
  ErrorKind,
  InteractionArea,
  LoadOutcome,
  PaymentContainerModeEvent,
  RedirectBlockReason,
  ReturnUnmatchedReason,
  SigninFailureReason,
} from './generated/analyticsEvents'
export type { ClientAnalyticsEventName, ClientAnalyticsEventProps }

// `ErrorName` is the one enum-like type that is NOT a closed set: it is the raw
// `err.name` off the throwable, forwarded verbatim — see errorClass.ts.
export type { ErrorName }

/**
 * Typed emit for any schema-declared client event (RFC-1577 §7).
 *
 * The event name must exist in `schemas/analytics/events/`, the properties must
 * match its generated type, and the trust tier (authenticated ⇒ attach the JWT)
 * comes from the schema rather than the call site. Prefer a named `track*`
 * helper where one exists; use this for event families with many call sites
 * (e.g. the canvas palette).
 */
export function track<N extends ClientAnalyticsEventName>(
  name: N,
  props: ClientAnalyticsEventProps[N],
): boolean {
  return emitEvent(name, CLIENT_ANALYTICS_EVENT_AUTHENTICATED[name], props)
}

// --- P0: authenticated ---
//
// Downloads: `canvas_video_download_initiated` is superseded by
// `asset_download_action` (RFC-1577) — see assetDownloadAnalytics.ts.

export function trackSignedIn(props: AuthSignedInProps): void {
  track('auth_signed_in', props)
}

// --- P0: pre-auth (acquisition funnel) ---

export function trackLandingPageView(props: LandingPageViewProps): void {
  track('landing_page_view', props)
}

export function trackLandingPromptConsoleStarted(props: LandingPagePromptConsoleStartedProps): void {
  track('landing_page_prompt_console_started', props)
}

export function trackLandingPromptSubmitted(props: LandingPagePromptSubmittedProps): void {
  track('landing_page_prompt_submitted', props)
}

export function trackSignupClicked(props: AuthSignupClickedProps): void {
  track('auth_signup_clicked', props)
}

export function trackSignupSubmitted(props: AuthSignupSubmittedProps): void {
  track('auth_signup_submitted', props)
}

// --- P1 ---

export function trackSigninFailed(props: AuthSigninFailedProps): void {
  track('auth_signin_failed', props)
}

export function trackVariantKept(props: CanvasVariantKeptProps): void {
  track('canvas_variant_kept', props)
}

export function trackClipAddedToTimeline(props: CanvasClipAddedToTimelineProps): void {
  track('canvas_clip_added_to_timeline', props)
}

export function trackNuxTooltipShown(props: NuxTooltipShownProps): void {
  track('nux_tooltip_shown', props)
}

export function trackNuxTooltipDismissed(props: NuxTooltipDismissedProps): void {
  track('nux_tooltip_dismissed', props)
}

// --- App-health (RFC-0974 follow-up, spec §5) ---
//
// Every helper injects `app_version` (present on every event, §4). The five
// in-session signals are AUTHENTICATED (verified JWT ⇒ server-derived user_id);
// `app_inp_reported` is anonymous page-hide INP delivered via sendBeacon.

/**
 * §5.1 — session denominator; fires once per session after auth restoration.
 * Returns whether the event was accepted for emission (env + consent) so the
 * caller only records the once-per-session marker on a real emission.
 */
export function trackAppSessionStarted(): boolean {
  return track('app_session_started', { app_version: getAppVersion() })
}

/** §5.2 — uncaught JS error / unhandled rejection. `error_name` is the raw class
 *  name (see `errorClass.ts`); `error_site` is a build-output coordinate
 *  (`chunk.js:line:col`) or a sentinel (see `errorSite.ts`). Still no message,
 *  stack, or rejection value. */
export function trackAppErrorUncaught(props: Omit<AppErrorUncaughtProps, 'app_version'>): void {
  track('app_error_uncaught', { ...props, app_version: getAppVersion() })
}

/** §5.3 — canvas node UI crash (React error boundary). project_id is required. */
export function trackCanvasNodeRenderFailed(props: Omit<CanvasNodeRenderFailedProps, 'app_version'>): void {
  track('canvas_node_render_failed', { ...props, app_version: getAppVersion() })
}

/** §5.4 — canvas load reached a terminal outcome (ready/error/timeout@30s). */
export function trackCanvasLoadFinished(props: Omit<CanvasLoadFinishedProps, 'app_version'>): void {
  track('canvas_load_finished', { ...props, app_version: getAppVersion() })
}

/** §5.5 — canvas image/video load reached a terminal outcome (@15s timeout). */
export function trackCanvasAssetLoadFinished(props: Omit<CanvasAssetLoadFinishedProps, 'app_version'>): void {
  track('canvas_asset_load_finished', {
    ...props,
    app_version: getAppVersion(),
  })
}

/**
 * §5.6 — anonymous INP report on page-hide. Delivered via sendBeacon (no auth
 * header, session_id only — NEVER user_id/project_id). Multiple reports per
 * document are permitted (a hidden page can resume); the platform picks the
 * latest per `session_id + page_id`.
 */
export function trackAppInpReported(props: Omit<AppInpReportedProps, 'app_version'>): void {
  emitBeacon('app_inp_reported', { ...props, app_version: getAppVersion() })
}

// --- Lifecycle ---

/**
 * Call on sign-out: sever the analytics session (drop pending events + rotate to
 * a fresh anonymous session), so post-logout browsing / a next user in the same
 * tab starts a new session and no in-flight authenticated event carries over.
 */
export function analyticsOnSignOut(): void {
  severSession()
}

// --- Purchase funnel (#3503) ---
//
// Spec: the "Payment Data" tab of the Dreamwrite [Canonical] doc, under
// "Logging Events Definition".
//
// These deliberately keep the SPEC's `surface.object.action` wire names rather
// than the view-prefixed snake_case used above. The analytics consumers
// specified these names and are building against them; migrating the ~30
// existing events to match would break every dashboard already keyed on the old
// ones. So the two conventions coexist on the wire, on purpose. The doc raised
// this as an open question — this is the answer.
//
// Three rules from the spec that the types below enforce structurally:
//
//   1. Money is an integer minor unit PLUS its ISO 4217 currency, never a float
//      and never a bare number. `amount_minor` and `currency` therefore always
//      travel together in the same payload.
//   2. Every amount-bearing event must say whether the amount is real money.
//      Once an order exists that is `payment_container_mode` (TEST | LIVE),
//      minted server-side with the handoff. BEFORE that — at catalog and
//      pay-now time — no container mode exists to report, so those events carry
//      `source` (placeholder | live) instead, which is the discriminator that
//      is actually knowable there. Neither may be omitted: an amount with no
//      indication of which it is gets summed into revenue during validation.
//   3. Never emit `handoff_code` (a single-use bearer credential), card data,
//      the app secret, or a raw email. There is deliberately no parameter on
//      any helper here that could carry one.

// Intent — before any money surface is opened.

export function trackInsufficientTokensBlock(props: CanvasInsufficientTokensBlockProps): void {
  track('canvas.insufficient_tokens.block', props)
}

export function trackUsageAndTokensView(props: SettingsUsageAndTokensViewProps): void {
  track('settings.usage_and_tokens.view', props)
}

// Catalog — what the user is offered.

/**
 * Double-logged against the API's `token_catalog.fetch.success` on purpose, so
 * client-side loss between the two is measurable rather than assumed. The API
 * half does not exist yet (`apps/api` has no analytics emitter), so for now this
 * is the only catalog signal.
 */
export function trackTokenCatalogView(props: SettingsTokenCatalogViewProps): void {
  track('settings.token_catalog.view', props)
}

/**
 * `source` is required, not optional. The click carries an `amount_minor`, and
 * the placeholder catalog's prices are invented — counting them alongside live
 * ones is the same mistake as counting TEST orders as revenue. There is no
 * `payment_container_mode` to give here: no order exists yet, and the mode is
 * minted server-side with the handoff.
 */
export function trackTokenCatalogOptionClick(props: TokenCatalogOptionClickProps): void {
  track('token_catalog.option.click', props)
}

// Order and handoff — Dreamwrite creates the DCP order.

/** `source` required for the same reason as the catalog click above. */
export function trackCheckoutInitiateClick(props: CheckoutInitiateClickProps): void {
  track('checkout.initiate.click', props)
}

/**
 * The last event before the user leaves our origin. Everything between this and
 * a `checkout.return.*` happens on Meta's side, where we cannot see.
 */
export function trackCheckoutRedirectSubmit(props: CheckoutRedirectSubmitProps): void {
  track('checkout.redirect.submit', props)
}

export function trackCheckoutRedirectBlock(props: CheckoutRedirectBlockProps): void {
  track('checkout.redirect.block', props)
}

// Return — the user comes back to our origin.

/**
 * Records ARRIVAL on the return URL and nothing more.
 *
 * Not proof of payment: the return is an unauthenticated top-level navigation
 * to a static URL carrying no signed outcome, so any funnel that reports
 * revenue off this event will over-count. Payment confirmation will come from
 * the settle-stage events, which are blocked on the fulfilment contract (#3447).
 */
export function trackCheckoutReturnSuccess(props: CheckoutReturnSuccessProps): void {
  track('checkout.return.success', props)
}

export function trackCheckoutReturnFail(props: CheckoutReturnFailProps): void {
  track('checkout.return.fail', props)
}

export function trackCheckoutReturnUnmatched(props: CheckoutReturnUnmatchedProps): void {
  track('checkout.return.unmatched', props)
}
